import moment from 'moment'

// National PEWS (NHS England, 2023) for patients under 18.
// Thresholds transcribed from the official observation and escalation charts:
// https://www.england.nhs.uk/publication/national-pews-observation-and-escalation-charts/
// Scored parameters: resp rate, respiratory distress, SpO2, oxygen delivery,
// heart rate, systolic BP, CRT. Temperature, AVPU and GCS are recorded but not
// scored. Parameters score 0, 1, 2 or 4 (there is no 3 in National PEWS).
// Keep in sync with mobile/src/components/EPCR/pews.js.

// Each band list is [upperBoundExclusive, score], ascending — a value scores
// the first band it sits below.
const BANDS = {
  '0-11m': {
    respRate: [
      [10, 4],
      [20, 2],
      [30, 1],
      [50, 0],
      [60, 1],
      [70, 2],
      [Infinity, 4],
    ],
    heartRate: [
      [80, 4],
      [90, 2],
      [110, 1],
      [150, 0],
      [170, 1],
      [180, 2],
      [Infinity, 4],
    ],
    bpSys: [
      [50, 4],
      [60, 2],
      [70, 1],
      [90, 0],
      [100, 1],
      [110, 2],
      [Infinity, 4],
    ],
  },
  '1-4y': {
    respRate: [
      [10, 4],
      [20, 2],
      [40, 0],
      [50, 1],
      [60, 2],
      [Infinity, 4],
    ],
    heartRate: [
      [60, 4],
      [70, 2],
      [90, 1],
      [140, 0],
      [150, 1],
      [170, 2],
      [Infinity, 4],
    ],
    bpSys: [
      [50, 4],
      [60, 2],
      [80, 1],
      [100, 0],
      [120, 1],
      [130, 2],
      [Infinity, 4],
    ],
  },
  '5-12y': {
    respRate: [
      [10, 4],
      [15, 2],
      [20, 1],
      [25, 0],
      [40, 1],
      [50, 2],
      [Infinity, 4],
    ],
    heartRate: [
      [60, 4],
      [70, 2],
      [80, 1],
      [120, 0],
      [140, 1],
      [160, 2],
      [Infinity, 4],
    ],
    bpSys: [
      [70, 4],
      [80, 2],
      [90, 1],
      [110, 0],
      [120, 1],
      [130, 2],
      [Infinity, 4],
    ],
  },
  '13-17y': {
    respRate: [
      [10, 4],
      [15, 1],
      [25, 0],
      [30, 1],
      [40, 2],
      [Infinity, 4],
    ],
    heartRate: [
      [50, 4],
      [60, 2],
      [70, 1],
      [100, 0],
      [120, 1],
      [130, 2],
      [Infinity, 4],
    ],
    bpSys: [
      [80, 4],
      [90, 2],
      [100, 1],
      [120, 0],
      [130, 1],
      [140, 2],
      [Infinity, 4],
    ],
  },
}

// SpO2 scores identically across all four charts
const SPO2_BANDS = [
  [92, 4],
  [95, 1],
  [Infinity, 0],
]

const RESP_DISTRESS_SCORES = { None: 0, Mild: 1, Moderate: 2, Severe: 4 }

export const RESP_DISTRESS_OPTIONS = ['None', 'Mild', 'Moderate', 'Severe']
export const RESP_SUPPORT_OPTIONS = ['None', 'High Flow', 'CPAP', 'BiPAP']

// Chart derogation codes for when a BP could not be recorded — the score can
// still complete without a systolic reading
export const BP_DEROGATION_OPTIONS = [
  'Not attempted (no concern)',
  'Unsuccessful attempt (no concern)',
  'Unsuccessful attempt (concern)',
]

// Age bands for the four charts; the estimated option list backs the picker
// shown when a paediatric patient's date of birth is unknown
export const AGE_BAND_OPTIONS = [
  { value: '0-11m', label: '0-11 months' },
  { value: '1-4y', label: '1-4 years' },
  { value: '5-12y', label: '5-12 years' },
  { value: '13-17y', label: '13-17 years' },
]

const scoreFromBands = (bands, value) =>
  bands.find(([max]) => Number(value) < max)[1]

// Oxygen delivery, by concentration: air/<21% scores 0; 21-29% 1; 30-49% 2;
// ≥50% 4. High Flow, CPAP and BiPAP score the chart maximum of 4. On oxygen
// with no concentration recorded scores 2 until one is documented.
const oxygenScore = (obs) => {
  const onOxygen = obs?.suppO2 === 'Yes' || obs?.suppO2 === true
  if (!onOxygen) return 0
  if (['High Flow', 'CPAP', 'BiPAP'].includes(obs?.respSupport)) return 4
  const fio2 = Number(obs?.suppO2Fio2)
  if (!obs?.suppO2Fio2 || Number.isNaN(fio2)) return 2
  if (fio2 >= 50) return 4
  if (fio2 >= 30) return 2
  if (fio2 >= 21) return 1
  return 0
}

// Systolic BP scores from the age band, or from the derogation code when no
// reading exists ('Unsuccessful attempt (concern)' scores 4, the rest 0)
const bpScore = (obs, bands) => {
  if (obs?.bpSys) return scoreFromBands(bands.bpSys, obs.bpSys)
  return obs?.bpDerogation === 'Unsuccessful attempt (concern)' ? 4 : 0
}

// Temperature isn't scored, but the chart flags it: ≥38°C red, ≤36°C blue
export const pewsTempWarning = (temp) => {
  if (temp === undefined || temp === null || temp === '') return null
  const t = Number(temp)
  if (Number.isNaN(t)) return null
  if (t >= 38) return '#dc0000'
  if (t <= 36) return '#0079c2'
  return null
}

// Age at the time of the obs decides which chart applies; null for adults
export const pewsAgeBand = (dob, atTime) => {
  if (!dob) return null
  const months = moment(atTime ?? undefined).diff(moment(dob), 'months')
  if (months < 0) return null
  if (months < 12) return '0-11m'
  if (months < 5 * 12) return '1-4y'
  if (months < 13 * 12) return '5-12y'
  if (months < 18 * 12) return '13-17y'
  return null
}

// Matches the paediatric test used elsewhere (e.g. Plan/PaediatricCO.jsx).
// atTime lets submitted records keep their PEWS column after the patient
// turns 18.
export const isUnder18 = (demographics, atTime) =>
  (demographics?.dobUnknown && demographics?.dobUnknownButPaediatric) ||
  (!!demographics?.dob &&
    moment(demographics.dob)
      .add(18, 'years')
      .isAfter(atTime ? moment(atTime) : moment()))

// The chart the score runs against: from the DOB when known, otherwise the
// estimated age group recorded on the obs set (unknown-DOB paediatric)
export const resolvePewsAgeBand = (demographics, obs) => {
  if (demographics?.dob) return pewsAgeBand(demographics.dob, obs?.time)
  if (demographics?.dobUnknown && demographics?.dobUnknownButPaediatric)
    return BANDS[obs?.estimatedAgeBand] ? obs.estimatedAgeBand : null
  return null
}

export const hasPewsRequiredFields = (obs) =>
  !!(
    obs?.respRate &&
    obs?.spo2 &&
    (obs?.bpSys || obs?.bpDerogation) &&
    obs?.heartRate &&
    obs?.capRefill &&
    obs?.respDistress &&
    obs?.suppO2
  )

export default ({ obs, ageBand }) => {
  const bands = BANDS[ageBand]
  if (!bands || !hasPewsRequiredFields(obs)) return false

  const score =
    scoreFromBands(bands.respRate, obs.respRate) +
    (RESP_DISTRESS_SCORES[obs.respDistress] ?? 0) +
    scoreFromBands(SPO2_BANDS, obs.spo2) +
    oxygenScore(obs) +
    scoreFromBands(bands.heartRate, obs.heartRate) +
    bpScore(obs, bands) +
    (Number(obs.capRefill) >= 3 ? 2 : 0)

  // Escalation levels: 1-4 Low, 5-8 Medium, 9-12 High, ≥13 Emergency
  let color
  if (score >= 9)
    color = '#dc0000' // Red — High/Emergency
  else if (score >= 5)
    color = '#fd8c00' // Orange — Medium
  else if (score >= 1)
    color = '#fdc500' // Yellow — Low
  else color = '#00ac46' // Green

  const textColor = score >= 9 || score === 0 ? '#fff' : 'inherit'

  return { score, color, textColor }
}
